// Constants and pure helpers extracted from EditClaimPanel for reuse + maintainability.

export const DOCUMENT_TYPES = [
  { label: 'Pictures Of Vehicle / Property Damage(s)', field: 'damage_pics' },
  { label: 'DL or CDL, Insurance, Registration of Other Vehicle', field: 'other_vehicle_docs' },
  { label: 'DOT#, Truck#, Plate#, Trailer# of Other CMV', field: 'other_cmv' },
  { label: 'Police Report # or Exchange Form', field: 'pr_or_ef' },
  { label: 'Company Truck Reg., Lease Agreement, Rate Con., BOL', field: 'our_truck_docs' },
  { label: 'Video Footage', field: 'video_footage' },
  { label: 'Drivers Written Statement', field: 'drivers_ws' },
  { label: 'Damage Repair Estimate(s)', field: 'estimates' },
  { label: 'Other Supporting Docs', field: 'other_docs' },
  { label: '"Log Books"', field: 'log_books' },
  { label: 'Drug & Alcohol Test Results', field: 'da_test' },
];

export const LOSS_TYPE_LABELS = {
  al: 'Auto Liability',
  pd: 'Physical Damage',
  cd: 'Cargo Claim',
  ocac: 'Occupational Accident',
  wc: 'Workers Compensation',
};

export const LOSS_TYPE_KEYS = ['al', 'pd', 'cd', 'ocac', 'wc'];

export const defaultTypeData = () => ({
  status: 'New',
  submittal: 'Pending',
  initial_amount: '',
  final_amount: '',
  other_party: '',
  in_house_claim_no: '',
  contact: '',
  contact_phone: '',
  extension: '',
  contact_email: '',
  insurance_company: '',
  insurance_claim_no: '',
  adjuster: '',
  adjusters_phone: '',
  adjusters_extension: '',
  adjusters_email: '',
});

export const getTimeAgo = (dateStr, byName) => {
  if (!dateStr) return '';
  const now = new Date();
  const updated = new Date(dateStr);
  const diffMs = now - updated;
  const diffMins = Math.floor(diffMs / 60000);
  let text;
  if (diffMins < 60) text = `Updated ${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  else {
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) text = `Updated ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    else {
      const diffDays = Math.floor(diffHours / 24);
      text = `Updated ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  }
  if (byName) text += ` by ${byName}`;
  return text + '.';
};

export const formatCurrency = (val) => {
  if (!val && val !== 0) return '';
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const parseCurrency = (str) => str.replace(/[^0-9.]/g, '');

/** Strip everything except digits from a phone string. */
export const rawDigits = (str) => String(str || '').replace(/\D/g, '');

/** Extract up to 10 digits from a phone string (canonical storage form). */
export const parsePhone = (str) => rawDigits(str).slice(0, 10);

/** Format a phone string as "(XXX) XXX-XXXX" progressively as digits are typed. */
export const formatPhone = (value) => {
  const digits = rawDigits(value).slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Map legacy claim-status 'Open' → canonical 'New'. Pass-through otherwise. */
export const normalizeStatus = (status) => (status === 'Open' ? 'New' : status);

export const isValidEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
export const isValidPhone = (str) => rawDigits(str).length === 10;

/**
 * Builds the form-data state object from a raw claim document.
 * Hydrates per-loss-type subfields from flat columns to support legacy/imported claims.
 */
export const buildFormDataFromClaim = (c) => {
  const existingTd = c.type_data || {};
  const type_data = {};
  LOSS_TYPE_KEYS.forEach(k => {
    if (existingTd[k]) {
      type_data[k] = { ...defaultTypeData(), ...existingTd[k] };
      type_data[k].status = normalizeStatus(type_data[k].status);
    } else {
      type_data[k] = defaultTypeData();
    }
  });
  LOSS_TYPE_KEYS.forEach(k => {
    if (!c[k]) return;
    const td = type_data[k] || {};
    const pick = (nestedKey, ...flatKeys) => {
      if (td[nestedKey] !== undefined && td[nestedKey] !== '' && td[nestedKey] !== null) return td[nestedKey];
      for (const fk of flatKeys) {
        if (c[fk] !== undefined && c[fk] !== '' && c[fk] !== null) return c[fk];
      }
      return '';
    };
    type_data[k] = {
      ...td,
      status: pick('status', `${k}_status`, 'status') || 'New',
      submittal: pick('submittal', `${k}_submittal`, 'submittal') || 'Pending',
      initial_amount: pick('initial_amount', `${k}_initial_amount`, 'initial_amount'),
      final_amount: pick('final_amount', `${k}_final_amount`, 'final_amount'),
      other_party: pick('other_party', `${k}_other_party`, 'other_party'),
      in_house_claim_no: pick('in_house_claim_no', `${k}_in_house_claims_no`, `${k}_in_house_claim_no`, 'in_house_claim_no'),
      contact: pick('contact', `${k}_contact`, 'contact'),
      contact_phone: pick('contact_phone', `${k}_contact_ph_no`, `${k}_contact_phone`, 'contact_phone'),
      extension: pick('extension', `${k}_contact_ph_ext`, `${k}_extension`, 'extension'),
      contact_email: pick('contact_email', `${k}_contact_email`, 'contact_email'),
      insurance_company: pick('insurance_company', `${k}_ins_company`, `${k}_insurance_company`, 'insurance_company'),
      insurance_claim_no: pick('insurance_claim_no', `${k}_claims_no`, `${k}_insurance_claim_no`, 'insurance_claim_no'),
      adjuster: pick('adjuster', `${k}_adjuster`, 'adjuster'),
      adjusters_phone: pick('adjusters_phone', `${k}_adjusters_ph_no`, `${k}_adjusters_phone`, 'adjusters_phone'),
      adjusters_extension: pick('adjusters_extension', `${k}_adjusters_ph_ext`, `${k}_adjusters_extension`, 'adjusters_extension'),
      adjusters_email: pick('adjusters_email', `${k}_adjusters_email`, 'adjusters_email'),
    };
  });
  return {
    company_name: c.company_name || '',
    date_of_loss: c.date_of_loss ? c.date_of_loss.split('T')[0] : '',
    drivers_name: c.drivers_name || '',
    drivers_ph_no: c.drivers_ph_no || '',
    truck_no: c.truck_no || '',
    trailer_no: c.trailer_no || '',
    driver_charged: c.driver_charged || '',
    deductible: c.deductible || false,
    deductible_amount: c.deductible_amount || '',
    other_amount_checked: c.other_amount_checked || false,
    other_amount: c.other_amount || '',
    location: c.location || '',
    description: c.description || '',
    al: c.al || false,
    pd: c.pd || false,
    cd: c.cd || false,
    ocac: c.ocac || false,
    wc: c.wc || false,
    other_vehicle_involved: c.other_vehicle_involved || '',
    fault: c.fault || '',
    police_involved: c.police_involved === true ? 'Yes' : c.police_involved === false ? 'No' : '',
    police_report_no: c.police_report_no || '',
    police_report_not_ready: c.police_report_not_ready || false,
    citation: c.citation || '',
    injury: c.injury === true || c.injury === 'true',
    tow_away: c.tow_away === true || c.tow_away === 'true',
    fatality: c.fatality === true || c.fatality === 'true',
    dot_recordable: c.dot_recordable === true || c.dot_recordable === 'true',
    damage_pics: c.damage_pics === true || c.damage_pics === 'true',
    other_vehicle_docs: c.other_vehicle_docs === true || c.other_vehicle_docs === 'true',
    other_cmv: c.other_cmv === true || c.other_cmv === 'true',
    pr_or_ef: c.pr_or_ef === true || c.pr_or_ef === 'true',
    our_truck_docs: c.our_truck_docs === true || c.our_truck_docs === 'true',
    video_footage: c.video_footage === true || c.video_footage === 'true',
    drivers_ws: c.drivers_ws === true || c.drivers_ws === 'true',
    estimates: c.estimates === true || c.estimates === 'true',
    other_docs: c.other_docs === true || c.other_docs === 'true',
    log_books: c.log_books === true || c.log_books === 'true',
    da_test: c.da_test === true || c.da_test === 'true',
    selected_documents: c.selected_documents || [],
    type_data,
  };
};
